class LeRobotPage extends HTMLElement {
  connectedCallback() {
    this.host = this.querySelector('[data-lerobot-host]');
    this.desktopTemplate = this.querySelector('[data-lerobot-desktop-template]');

    if (!this.host || !this.desktopTemplate) return;

    this.mobileView = this.host.firstElementChild;
    this.desktopView = null;
    this.activeMode = null;
    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleAnchorClick = this.handleAnchorClick.bind(this);

    this.handleViewportChange(this.mediaQuery);
    this.mediaQuery.addEventListener('change', this.handleViewportChange);
    this.addEventListener('click', this.handleAnchorClick);
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.handleViewportChange);
    this.removeEventListener('click', this.handleAnchorClick);
    this.destroyActiveVariant();
  }

  handleAnchorClick(event) {
    const link = event.target.closest('a[href^="#lerobot-"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!['#lerobot-projects', '#lerobot-nexarm'].includes(hash)) return;

    const target = this.host.querySelector(hash);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
    window.history.pushState(null, '', hash);
  }

  handleViewportChange(event) {
    const nextMode = event.matches ? 'desktop' : 'mobile';

    if (nextMode === this.activeMode) return;

    this.dispatchEvent(new CustomEvent('lerobot:before-variant-change', {
      bubbles: true,
      detail: { currentMode: this.activeMode, nextMode }
    }));

    this.destroyActiveVariant();

    if (nextMode === 'desktop') {
      if (!this.desktopView) {
        this.desktopView = this.desktopTemplate.content.firstElementChild?.cloneNode(true);
      }

      if (this.desktopView) {
        this.host.replaceChildren(this.desktopView);
      }
    } else if (this.mobileView) {
      this.host.replaceChildren(this.mobileView);
    }

    this.activeMode = nextMode;
    this.dataset.activeMode = nextMode;
    this.initializeActiveVariant();

    this.dispatchEvent(new CustomEvent('lerobot:variant-change', {
      bubbles: true,
      detail: { mode: nextMode }
    }));
  }

  initializeActiveVariant() {
    if (this.activeMode !== 'desktop') return;

    this.revealTrack = this.host.querySelector('[data-lerobot-reveal-track]');
    this.revealSteps = this.revealTrack
      ? Array.from(this.revealTrack.querySelectorAll('[data-lerobot-reveal-step]'))
      : [];
    this.animationFrame = null;

    this.requestDesktopUpdate = this.requestDesktopUpdate.bind(this);

    window.addEventListener('scroll', this.requestDesktopUpdate, { passive: true });
    window.addEventListener('resize', this.requestDesktopUpdate);

    this.requestDesktopUpdate();
  }

  destroyActiveVariant() {
    window.removeEventListener('scroll', this.requestDesktopUpdate);
    window.removeEventListener('resize', this.requestDesktopUpdate);

    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = null;
  }

  requestDesktopUpdate() {
    if (this.animationFrame) return;

    this.animationFrame = window.requestAnimationFrame(() => {
      this.updateRevealProgress();
      this.animationFrame = null;
    });
  }

  updateRevealProgress() {
    if (!this.revealTrack || !this.revealSteps.length) return;

    const rect = this.revealTrack.getBoundingClientRect();
    const scrollRange = Math.max(this.revealTrack.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(1, Math.max(0, -rect.top / scrollRange));
    const stepStarts = [0.04, 0.18, 0.36, 0.62, 0.78];

    this.revealSteps.forEach((element) => {
      const step = Number(element.dataset.lerobotRevealStep || 0);
      const start = stepStarts[step] ?? stepStarts[stepStarts.length - 1];
      const exitAt = Number.parseFloat(element.dataset.lerobotRevealExitAt);
      const hasEntered = progress >= start;
      const hasExited = Number.isFinite(exitAt) && progress >= exitAt;
      const translateY = hasEntered ? 0 : 28;

      element.style.opacity = hasEntered && !hasExited ? '1' : '0';
      element.style.transform = element.classList.contains('lerobot-reveal__main-title')
        ? `translate(-50%, ${translateY}px)`
        : `translateY(${translateY}px)`;
    });
  }

}

if (!customElements.get('lerobot-page')) {
  customElements.define('lerobot-page', LeRobotPage);
}
